import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtMoney, errMsg } from "@/lib/format";
import { useCategories } from "@/hooks/useCategories";
import { toast } from "sonner";
import { Plus, Trash2, Target as TargetIcon, Wallet, TrendingUp, Edit2, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function BudgetsPage() {
  const [budgets, setBudgets] = useState([]);
  const [goals, setGoals] = useState([]);
  const [txs, setTxs] = useState([]);
  const [openB, setOpenB] = useState(false);
  const [openG, setOpenG] = useState(false);

  const load = async () => {
    try {
      const [b, g, t] = await Promise.all([
        api.get("/budgets"), api.get("/goals"),
        api.get("/transactions", { params: { type: "expense", limit: 500 } }),
      ]);
      setBudgets(b.data); setGoals(g.data); setTxs(t.data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const spentOf = (cat) => {
    const month = new Date().toISOString().slice(0, 7);
    return txs.filter(t => t.category === cat && (t.date || "").startsWith(month))
      .reduce((s, t) => s + t.amount, 0);
  };

  const delBudget = async (id) => {
    if (!window.confirm("¿Eliminar presupuesto?")) return;
    try { await api.delete(`/budgets/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const delGoal = async (id) => {
    if (!window.confirm("¿Eliminar meta?")) return;
    try { await api.delete(`/goals/${id}`); toast.success("Eliminada"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const addToGoal = async (g, delta) => {
    const newSaved = Math.max(0, g.saved + delta);
    try {
      await api.patch(`/goals/${g.id}`, { saved: newSaved });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-8" data-testid="budgets-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Planificación</div>
        <h1 className="text-2xl sm:text-3xl font-bold mt-1">Presupuestos & Metas</h1>
      </div>

      {/* Budgets */}
      <section data-testid="budgets-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2"><Wallet className="w-4 h-4 text-violet-400"/>Presupuestos del mes</h2>
          <button onClick={() => setOpenB(true)} data-testid="add-budget-btn" className="flex items-center gap-2 text-sm bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-2 rounded-lg">
            <Plus className="w-4 h-4"/> Nuevo
          </button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {budgets.length === 0 ? (
            <div className="card-surface p-6 text-slate-400 text-sm col-span-full text-center">Sin presupuestos. Crea uno para empezar.</div>
          ) : budgets.map(b => {
            const spent = spentOf(b.category);
            const pct = Math.min(100, (spent / b.limit) * 100);
            const danger = pct >= 90;
            return (
              <div key={b.id} className="card-surface p-5 group" data-testid={`budget-card-${b.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-white">{b.category}</div>
                    <div className="text-xs text-slate-500 mt-0.5">Mensual</div>
                  </div>
                  <button onClick={() => delBudget(b.id)} data-testid={`delete-budget-${b.id}`} className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-rose-400 p-1.5 rounded-lg transition-all">
                    <Trash2 className="w-3.5 h-3.5"/>
                  </button>
                </div>
                <div className="mt-4 flex items-baseline justify-between">
                  <div className="text-xl font-bold">{fmtMoney(spent)}</div>
                  <div className="text-xs text-slate-500">/ {fmtMoney(b.limit)}</div>
                </div>
                <div className="mt-3 h-2 rounded-full bg-white/5 overflow-hidden">
                  <div className={`h-full rounded-full ${danger ? "bg-gradient-to-r from-rose-500 to-rose-400" : "bg-gradient-to-r from-violet-500 to-fuchsia-400"}`} style={{ width: `${pct}%` }}/>
                </div>
                <div className="mt-2 text-xs text-slate-500">{pct.toFixed(0)}% usado</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Goals */}
      <section data-testid="goals-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2"><TargetIcon className="w-4 h-4 text-violet-400"/>Metas de ahorro</h2>
          <button onClick={() => setOpenG(true)} data-testid="add-goal-btn" className="flex items-center gap-2 text-sm bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-2 rounded-lg">
            <Plus className="w-4 h-4"/> Nueva meta
          </button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {goals.length === 0 ? (
            <div className="card-surface p-6 text-slate-400 text-sm col-span-full text-center">Sin metas. Empieza a soñar en grande.</div>
          ) : goals.map(g => {
            const pct = Math.min(100, (g.saved / g.target) * 100);
            return (
              <div key={g.id} className="card-surface p-5 relative overflow-hidden group" data-testid={`goal-card-${g.id}`}>
                <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full opacity-20 blur-2xl" style={{ background: g.color }}/>
                <div className="relative">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${g.color}22` }}>
                        <TargetIcon className="w-4 h-4" style={{ color: g.color }}/>
                      </div>
                      <div className="text-sm font-semibold">{g.name}</div>
                    </div>
                    <button onClick={() => delGoal(g.id)} data-testid={`delete-goal-${g.id}`} className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-rose-400 p-1.5 rounded-lg transition-all">
                      <Trash2 className="w-3.5 h-3.5"/>
                    </button>
                  </div>
                  <div className="mt-4 text-2xl font-bold">{fmtMoney(g.saved)}</div>
                  <div className="text-xs text-slate-500">de {fmtMoney(g.target)}</div>
                  <div className="mt-3 h-2 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${g.color}, #C084FC)` }}/>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="text-xs text-slate-500">{pct.toFixed(0)}% completado</div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => addToGoal(g, 1000)} data-testid={`goal-add-${g.id}`}
                        className="text-[11px] bg-white/5 hover:bg-violet-500/20 hover:text-violet-300 px-2 py-1 rounded-md text-slate-300">+1,000</button>
                      <button onClick={() => addToGoal(g, 5000)} data-testid={`goal-add5-${g.id}`}
                        className="text-[11px] bg-white/5 hover:bg-violet-500/20 hover:text-violet-300 px-2 py-1 rounded-md text-slate-300">+5,000</button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <AddBudgetDialog open={openB} onOpenChange={setOpenB} onCreated={load} existing={budgets.map(b => b.category)} />
      <AddGoalDialog open={openG} onOpenChange={setOpenG} onCreated={load} />
    </div>
  );
}

function AddBudgetDialog({ open, onOpenChange, onCreated, existing }) {
  const { categories } = useCategories();
  const [category, setCategory] = useState("Comida");
  const [limit, setLimit] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (existing.includes(category)) { toast.error("Ya existe un presupuesto para esta categoría"); return; }
    try {
      await api.post("/budgets", { category, limit: Number(limit), period: "monthly" });
      toast.success("Presupuesto creado");
      setLimit(""); onCreated?.(); onOpenChange(false);
    } catch (e2) { toast.error(errMsg(e2)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#15141b] border-white/10 text-white sm:max-w-md" data-testid="add-budget-dialog">
        <DialogHeader><DialogTitle className="text-white">Nuevo presupuesto</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1.5">Categoría</div>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-white text-sm" data-testid="budget-category">
              {categories.filter(c => c.type === "expense").map(c => (
                <option key={c.id || c.value} value={c.value} className="bg-[#15141b]">{c.value}</option>
              ))}
            </select>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1.5">Límite mensual (RD$)</div>
            <input type="number" min="1" required value={limit} onChange={(e) => setLimit(e.target.value)} placeholder="5000" data-testid="budget-limit"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-white text-lg font-semibold"/>
          </div>
          <button type="submit" data-testid="budget-submit" className="w-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-semibold py-3 rounded-xl">Crear</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddGoalDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const [saved, setSaved] = useState("");
  const [color, setColor] = useState("#8B5CF6");
  const colors = ["#8B5CF6", "#38BDF8", "#10B981", "#F59E0B", "#F43F5E", "#EC4899"];
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/goals", { name, target: Number(target), saved: Number(saved || 0), color });
      toast.success("Meta creada");
      setName(""); setTarget(""); setSaved(""); onCreated?.(); onOpenChange(false);
    } catch (e2) { toast.error(errMsg(e2)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#15141b] border-white/10 text-white sm:max-w-md" data-testid="add-goal-dialog">
        <DialogHeader><DialogTitle className="text-white">Nueva meta de ahorro</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Ej: Vacaciones" data-testid="goal-name"
            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-white text-sm"/>
          <div className="grid grid-cols-2 gap-3">
            <input type="number" min="1" required value={target} onChange={(e) => setTarget(e.target.value)} placeholder="Meta (RD$)" data-testid="goal-target"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-white text-sm"/>
            <input type="number" min="0" value={saved} onChange={(e) => setSaved(e.target.value)} placeholder="Ahorrado (RD$)" data-testid="goal-saved"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-white text-sm"/>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-2">Color</div>
            <div className="flex gap-2">
              {colors.map(c => (
                <button type="button" key={c} onClick={() => setColor(c)} className={`w-8 h-8 rounded-full transition-all ${color === c ? "ring-2 ring-white ring-offset-2 ring-offset-[#15141b]" : ""}`} style={{ background: c }}/>
              ))}
            </div>
          </div>
          <button type="submit" data-testid="goal-submit" className="w-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-semibold py-3 rounded-xl">Crear meta</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
